import { cerrarSesionCompartida, instalarSalidaPestanas, SESION_CERRADA } from '../prod/runtime/sesion-salida';
import { prepararSesionPestana } from '../prod/runtime/sesion-pestanas';
/**
 * auth.tsx — AuthContext, useAuth hook, login/logout
 *
 * Authenticates against the app's own API endpoint (POST /api/auth/login,
 * ASP.NET Identity → JWT HS256) instead of AWS Amplify/Cognito.
 * Exposes a clean React context so components don't import the transport directly.
 *
 * Features:
 * - Session persistence across refreshes (token + user persisted in storage)
 * - Access token injection into api.ts (read on every request as Bearer)
 * - Permission helper: hasGroup(), isAdmin()
 * - Logout clears all local session state
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { User } from '@netcom/shared';
import { apiPost, setAccessToken, clearAccessToken, bindSessionUser, bindWindowBranch, NetworkError } from './api';

// Dev mode: skip the API login entirely when VITE_DEV_AUTH=true
const DEV_AUTH = import.meta.env.VITE_DEV_AUTH === 'true';

// sessionStorage key for the user (paired with kapi:access_token)
const USER_STORAGE_KEY = 'kapi:user';

// ---------------------------------------------------------------------------
// API login envelope (POST /api/auth/login)
// ---------------------------------------------------------------------------

/** User payload returned by the API on successful login. */
/** Sucursal accesible por el usuario (para el selector multi-sucursal). */
export interface BranchInfo {
  id: string;
  name: string;
  /** Empresa/marca (companies.Name): "DON JUAN", "COLLER", … */
  companyName?: string;
  /** Ruta del logo (company_branches.LogoRoute o el de la empresa); '' si no hay */
  logo?: string;
  db: string;
  isDefault?: boolean;
}

interface ApiLoginUser {
  id: string;
  username: string;
  email: string;
  /** Nombre de la persona (asp_net_users.Name) — el navbar del POS lo muestra */
  name?: string;
  roles: string[];
  // `id` es company_branches.ID (VARCHAR en prod; puede ser alfanumérico, p.ej.
  // '52_1'). Se trata como string — castear a número descartaba branches reales.
  branches: BranchInfo[];
}

/** Inner `response` of the login envelope. */
interface ApiLoginResponse {
  token: string;
  user: ApiLoginUser;
  /** N-PL16 / N2-PL02: a dónde entra (RedirectToModule de prod); null mientras falte elegir sucursal. */
  destination?: string | null;
}

// ---------------------------------------------------------------------------
// N-PL16 · Destino de llegada y textos del login (prod:WebApp/Scripts/App/Login/login.js:28-40)
// ---------------------------------------------------------------------------

/**
 * Destinos de llegada que la web ya dibuja. Los demás que manda el servidor (Centro de distribución, Órdenes de servicio,
 * Taller y Surtir por voz) caen en el menú hasta que exista su pantalla, en vez de terminar en /404.
 */
const DESTINOS_CON_PANTALLA = new Set(['/', '/pos', '/cfdi/dashboard']);

/** Ruta de la web para el `destination` del servidor; null si no vino. */
export function destinoEnWeb(destination: unknown): string | null {
  if (typeof destination !== 'string' || !destination.startsWith('/')) return null;
  return DESTINOS_CON_PANTALLA.has(destination) ? destination : '/';
}

/** Los dos textos con que el servidor responde a unas credenciales incorrectas (prod login.js:33). */
const CREDENCIALES_INCORRECTAS = ['La contraseña es incorrecta', 'El correo o la contraseña son incorrectos'];
/** prod login.js:34: un solo texto, que no dice si el correo existe. */
export const TEXTO_CREDENCIALES = 'El correo o la contraseña no son correctos. Revísalos e intenta de nuevo.';
/** prod login.js:35. */
export const TEXTO_SIN_CONEXION = 'No se pudo iniciar sesión. Revisa tu conexión e intenta de nuevo.';
/** prod login.js:37-40 (`textoDelMotivo`): cualquier otro motivo se muestra tal como lo manda el servidor. */
export function textoDelMotivo(mensaje: unknown): string {
  const texto = String(mensaje == null ? '' : mensaje).trim();
  return texto === '' || CREDENCIALES_INCORRECTAS.includes(texto) ? TEXTO_CREDENCIALES : texto;
}

/**
 * ¿El destino es un documento /sistema (plan §1.6)? Esos no son rutas de la SPA vieja: se entra con recarga completa.
 * Si entraran a BrowserRouter caerían en `*` y en /404 (App.tsx).
 */
export function esDestinoSistema(path: string): boolean {
  return /^\/sistema(\/|$|\?)/i.test(path);
}

/**
 * Lleva a la pantalla de llegada desde fuera del Router (AuthProvider envuelve a BrowserRouter): prod navega con
 * `window.location.href` (AccountController.cs:622-664); aquí se empuja la ruta y BrowserRouter la toma del popstate.
 * A un documento /sistema se va con `location.assign` (plan §1.6).
 */
export function irADestino(path: string | null): void {
  if (!path || window.location.pathname === path) return;
  if (esDestinoSistema(path) || esDestinoSistema(window.location.pathname)) {
    window.location.assign(path);
    return;
  }
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

const DEV_USER: User = {
  id: 'dev-user-001',
  name: 'Admin (Dev)',
  email: 'admin@netcom-dev.local',
  groups: ['admin', 'cashier', 'manager'],
  isAdmin: true,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthState {
  /** Current user — null when unauthenticated or loading */
  user: User | null;
  /** Sucursales accesibles (con nombre) — para el selector multi-sucursal */
  branches: BranchInfo[];
  /** Sucursal elegida esta sesión (id), o null si aún no elige (multi-sucursal) */
  selectedBranchId: string | null;
  /** True while initial session check is running */
  loading: boolean;
  /** Error from the most recent login/logout operation */
  error: string | null;
  /** N-PL16: pantalla de llegada del último login (ya resuelta para la web); null si aún no se sabe. */
  destination?: string | null;
  /**
   * La sesión terminó por un logout (de esta pestaña o avisado por otra). sesion-salida ya va al login SIN regreso, como
   * LogOff de prod (AccountController.cs:939-942): ProtectedRoute no debe navegar otra vez con ReturnUrl. Se queda hasta
   * el siguiente login o restauración; un 401 posterior no la quita.
   */
  sesionCerrada?: boolean;
}

export interface LoginCredentials {
  username: string;
  password: string;
  /** Se acepta por compatibilidad; la sesión siempre queda en la pestaña. */
  rememberMe?: boolean;
}

export interface AuthContextValue extends AuthState {
  /** Sign in with username + password */
  login(credentials: LoginCredentials): Promise<boolean>;
  /** Sign out — clears all local session state (token + user) */
  logout(): Promise<void>;
  /** Check if user is a system admin */
  isAdmin(): boolean;
  /** Check if user belongs to a specific group/role */
  hasGroup(group: string): boolean;
  /** Elegir la sucursal activa de la sesión (persiste en sessionStorage) */
  setBranch(branchId: string): void;
  /** Refresh the current session (renews access token) */
  refreshSession(): Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);
AuthContext.displayName = 'AuthContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Whether a set of groups grants system-wide admin (case-insensitive: el rol
 * real de prod es 'Admin'). */
function groupsImplyAdmin(groups: string[]): boolean {
  return groups.some((g) => {
    const x = g.toLowerCase();
    return x === 'admin' || x === 'admins';
  });
}

/**
 * Map the API login user → our App `User` type.
 *
 * `groups` mirrors the JWT `cognito:groups` claim built by the API:
 * roles + one `branch:<id>` per accessible branch. useBranchSession parses
 * those `branch:<id>` entries to resolve the active tenant (with a DEV
 * fallback when the user has no branch claims).
 */
function apiToAppUser(apiUser: ApiLoginUser): User {
  const roles = Array.isArray(apiUser.roles) ? apiUser.roles : [];
  const branches = Array.isArray(apiUser.branches) ? apiUser.branches : [];
  const groups = [...roles, ...branches.map((b) => `branch:${b.id}`)];
  return {
    id: apiUser.id,
    // Paridad navbar prod: muestra el NOMBRE de la persona ('Testing'), no el
    // email; fallback a username/email si el registro no tiene Name.
    name: (apiUser.name ?? '').trim() || apiUser.username || apiUser.email,
    email: apiUser.email,
    groups,
    isAdmin: groupsImplyAdmin(groups),
  };
}

/** Persist the resolved user alongside the access token. */
function persistUser(user: User): void {
  try {
    sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } catch {
    // Storage unavailable (private mode / quota) — token still drives auth.
  }
}

/** Read the persisted user, or null when absent/corrupt. */
function readPersistedUser(): User | null {
  try {
    const raw = sessionStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<User> | null;
    if (!parsed || typeof parsed.id !== 'string') return null;
    return {
      id: parsed.id,
      name: parsed.name ?? parsed.email ?? '',
      email: parsed.email ?? '',
      groups: Array.isArray(parsed.groups) ? parsed.groups : [],
      isAdmin: Boolean(parsed.isAdmin),
    };
  } catch {
    return null;
  }
}

/** sessionStorage key for the branch list (con nombres). */
const BRANCHES_STORAGE_KEY = 'kapi:branches';

/** sessionStorage key for the branch the user picked this session (multi-sucursal). */
export const SELECTED_BRANCH_KEY = 'kapi:selected_branch';

/** Persist the user's accessible branches (with names) for the selector. */
function persistBranches(branches: BranchInfo[]): void {
  try {
    sessionStorage.setItem(BRANCHES_STORAGE_KEY, JSON.stringify(branches));
  } catch {
    /* storage unavailable — selector cae a IDs del token */
  }
}

/** Read the branch selected this session (sessionStorage), or null. */
function readSelectedBranch(): string | null {
  try {
    return sessionStorage.getItem(SELECTED_BRANCH_KEY) || null;
  } catch {
    return null;
  }
}

/** Read the persisted branch list, or [] when absent/corrupt. */
function readPersistedBranches(): BranchInfo[] {
  try {
    const raw = sessionStorage.getItem(BRANCHES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((b): b is BranchInfo => !!b && typeof b.id === 'string')
      .map((b) => ({
        id: b.id,
        name: b.name ?? b.id,
        companyName: b.companyName ?? '',
        logo: b.logo ?? '',
        db: b.db ?? '',
        isDefault: Boolean(b.isDefault),
      }));
  } catch {
    return [];
  }
}

/** Read the access token from either storage tier (mirrors api.ts). */
function readAccessToken(): string | null {
  return (
    sessionStorage.getItem('kapi:access_token') ??
    null
  );
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [state, setState] = useState<AuthState>({
    user: null,
    branches: [],
    selectedBranchId: readSelectedBranch(),
    loading: true,
    error: null,
  });


  // -------------------------------------------------------------------------
  // Session restore on mount
  // -------------------------------------------------------------------------
  const restoreSession = useCallback(async () => {
    // Dev bypass: auto-login as admin
    if (DEV_AUTH) {
      setAccessToken('dev-token', true);
      setState({ user: DEV_USER, branches: [], selectedBranchId: null, loading: false, error: null });
      return;
    }

    // Real path: rehydrate from storage. api.ts attaches the token as Bearer,
    // so a persisted token + user is a complete session. No remote call here.
    await prepararSesionPestana();
    const token = readAccessToken();
    const user = readPersistedUser();

    if (token && user) {
      bindSessionUser(user.id);
      setState({ user, branches: readPersistedBranches(), selectedBranchId: readSelectedBranch(), loading: false, error: null });
      return;
    }

    // Incomplete (only one of the two present) → clear and stay logged out.
    clearAccessToken();
    setState({ user: null, branches: [], selectedBranchId: null, loading: false, error: null });
  }, []);

  useEffect(() => {
    const cerrada = () => setState({ user: null, branches: [], selectedBranchId: null, loading: false, error: null, sesionCerrada: true });
    window.addEventListener(SESION_CERRADA, cerrada);
    const detener = instalarSalidaPestanas();
    return () => { detener(); window.removeEventListener(SESION_CERRADA, cerrada); };
  }, []);

  useEffect(() => {
    void restoreSession();
  }, [restoreSession]);

  // -------------------------------------------------------------------------
  // Handle api.ts 401 → clear session so ProtectedRoute redirects via React Router
  // -------------------------------------------------------------------------
  useEffect(() => {
    function handleUnauthorized(): void {
      clearAccessToken();
      sessionStorage.removeItem(USER_STORAGE_KEY);
      sessionStorage.removeItem(BRANCHES_STORAGE_KEY);
      sessionStorage.removeItem(SELECTED_BRANCH_KEY);
      bindWindowBranch(null);
      setState(prev => ({ user: null, branches: [], selectedBranchId: null, loading: false, error: null, sesionCerrada: prev.sesionCerrada }));
    }
    window.addEventListener('kapi:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('kapi:unauthorized', handleUnauthorized);
  }, []);

  // -------------------------------------------------------------------------
  // Login
  // -------------------------------------------------------------------------
  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    // Dev bypass
    if (DEV_AUTH) {
      setAccessToken('dev-token', true);
      setState({ user: DEV_USER, branches: [], selectedBranchId: null, loading: false, error: null });
      return true;
    }

    const remember = false;
    localStorage.removeItem('kapi:remember_me');

    try {
      // POST to the app's own login endpoint (ASP.NET Identity → JWT).
      // Envelope: { isSuccess, response: { token, user }, message }.
      const envelope = await apiPost<ApiLoginResponse>('/api/auth/login', {
        username: credentials.username,
        password: credentials.password,
      });

      const ok = envelope?.isSuccess === true;
      const data = envelope?.response;
      const token = data?.token;
      const selected = (data as (ApiLoginResponse & { branchId?: string }) | undefined)?.branchId ?? null;

      if (ok && token && data?.user) {
        setAccessToken(token, remember);
        const user = apiToAppUser(data.user);
        bindSessionUser(user.id, true);
        persistUser(user);
        const branches = Array.isArray(data.user.branches) ? data.user.branches : [];
        persistBranches(branches);
        // Nueva sesión → olvidar cualquier sucursal elegida antes (re-preguntar).
        try { if (selected) sessionStorage.setItem(SELECTED_BRANCH_KEY, selected); else sessionStorage.removeItem(SELECTED_BRANCH_KEY); } catch { /* noop */ }
        // N-PL15: un login empieza una ventana nueva (prod entra con navegación completa); sin sucursal todavía, la
        // ventana toma la que se elija en el selector obligatorio.
        bindWindowBranch(selected);
        setState({ user, branches, selectedBranchId: selected, loading: false, error: null, destination: destinoEnWeb(data.destination) });
        return true;
      }

      // Business failure (isSuccess:false, or missing token/user on a 200).
      throw new Error(textoDelMotivo(envelope?.message));
    } catch (err) {
      // Transport/HTTP failure (401 throws ApiError with the API message). N-PL16: los dos textos de credenciales se
      // muestran como uno solo y una falla de red con el texto de prod (login.js:37-40,72-73).
      const message = err instanceof NetworkError ? TEXTO_SIN_CONEXION : textoDelMotivo(err instanceof Error ? err.message : '');
      setState(prev => ({ ...prev, loading: false, error: message }));
      return false;
    }
  }, []);

  // -------------------------------------------------------------------------
  // Logout
  // -------------------------------------------------------------------------
  const logout = useCallback(async (): Promise<void> => {
    cerrarSesionCompartida();
  }, []);

  // -------------------------------------------------------------------------
  // Refresh session
  // -------------------------------------------------------------------------
  const refreshSession = useCallback(async (): Promise<void> => {
    await restoreSession();
  }, [restoreSession]);

  // -------------------------------------------------------------------------
  // Permission helpers
  // -------------------------------------------------------------------------
  const isAdmin = useCallback((): boolean => {
    return state.user?.isAdmin ?? false;
  }, [state.user]);

  const hasGroup = useCallback((group: string): boolean => {
    return state.user?.groups.includes(group) ?? false;
  }, [state.user]);

  // -------------------------------------------------------------------------
  // Branch selection (sucursal activa de la sesión)
  // -------------------------------------------------------------------------
  const setBranch = useCallback((branchId: string): void => {
    // N2-PL02: elegir la sucursal sin tener una es el paso 2 del login y entra al destino; cambiarla dentro de la sesión
    // (selector del encabezado, facturación) se queda en la pantalla.
    const completaLogin = readSelectedBranch() === null;
    void apiPost<{ token: string; branchId: string; destination: string }>('/api/auth/select-branch', {
      token: readAccessToken(), branchId,
    }).then(result => {
      if (!result.isSuccess || !result.response) throw new Error(result.message || 'No se pudo seleccionar la sucursal.');
      setAccessToken(result.response.token);
      sessionStorage.setItem(SELECTED_BRANCH_KEY, result.response.branchId);
      const destination = destinoEnWeb(result.response.destination);
      setState(prev => ({ ...prev, selectedBranchId: result.response!.branchId, error: null, destination }));
      if (completaLogin) irADestino(destination);
    }).catch(error => setState(prev => ({ ...prev, error: error instanceof Error ? error.message : 'No se pudo seleccionar la sucursal.' })));
  }, []);

  // -------------------------------------------------------------------------
  // Context value
  // -------------------------------------------------------------------------
  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      isAdmin,
      hasGroup,
      setBranch,
      refreshSession,
    }),
    [state, login, logout, isAdmin, hasGroup, setBranch, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * useAuthOptional — access the auth context WITHOUT throwing when there is no
 * <AuthProvider> above (returns null). For shared layout chrome (ModuleShell)
 * that may be rendered in isolation (e.g. unit tests) but must not crash.
 */
export function useAuthOptional(): AuthContextValue | null {
  return useContext(AuthContext);
}

/**
 * useAuth — access the auth context.
 * Must be used inside <AuthProvider>.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return context;
}

/**
 * useRequireAuth — redirect to login if not authenticated.
 * Returns user if authenticated, null if still loading.
 */
export function useRequireAuth(): User | null {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      // Emit the same event so AuthProvider clears state and React Router redirects
      const returnTo = window.location.pathname + window.location.search;
      window.dispatchEvent(
        new CustomEvent('kapi:unauthorized', { detail: { returnTo } }),
      );
    }
  }, [user, loading]);

  return user;
}
